import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';
import isNil from 'lodash/isNil';
import { freeze } from '@reduxjs/toolkit';
import {
  FieldContext,
  FilterType,
  IEventInput,
  IFieldFieldsFragment,
  INamedFilterFieldsFragment,
  IRelationalOperatorFieldsFragment,
  LogicalOperatorKey,
  OperandType,
  RelationalOperatorKey,
} from '../../utils/apollo/graphqlSchema';
import { ZodProperties } from '../../utils/zod';
import { UnreachableCaseError } from 'ts-essentials';

export type IPredicateContext = {
  events: IEventInput[];
  fieldContext: FieldContext;
  func: string | null;
};

const relOpSchema = z.object<ZodProperties<IRelationalOperatorFieldsFragment>>({
  __typename: z.literal('RelationalOperator'),
  key: z.nativeEnum(RelationalOperatorKey),
  name: z.string(),
  supportCaseInsensitive: z.boolean(),
  values: z.array(z.string()),
});

const fieldSchema = z
  .object<
    ZodProperties<Pick<IFieldFieldsFragment, 'key' | 'name' | '__typename'>>
  >({
    __typename: z.literal('Field'),
    name: z.string(),
    key: z.string(),
  })
  // We perform very limited validation over the Field because React Hook Form does not edit its properties.
  // Moreover, React Hook Form performs complex type manipulation to expand objects (e.g. {a: {b: 1}}) into path ("a.b")
  // which easily reaches TypeScript limits, and we get "TS2589: Type instantiation is excessively deep and possibly
  // infinite" (e.g. https://github.com/react-hook-form/react-hook-form/issues/6679). Version 8 of RHF aims to solve
  // this performance regression (https://github.com/orgs/react-hook-form/discussions/7389)
  .passthrough();

const predicateOperandTypeEnum = z.enum([OperandType.Value, OperandType.Field]);

const basicFieldValueSchema = z.object({
  __typename: z.literal('BasicFieldValue'),
  name: z.string().min(1),
  key: z.string().min(1),
});

const anyFieldValueSchema = z.object({
  __typename: z.literal('AnyFieldValue'),
  name: z.string(),
});

const operandValueSchema = z.union([
  basicFieldValueSchema,
  anyFieldValueSchema,
]);

const valueOperandSchema = z.object({
  type: z.literal(predicateOperandTypeEnum.enum.VALUE),
  operands: z.array(operandValueSchema).nullable(),
  fromOperand: operandValueSchema.nullable(),
  toOperand: operandValueSchema.nullable(),
});

const fieldOperandSchema = z.object({
  type: z.literal(predicateOperandTypeEnum.enum.FIELD),
  field: fieldSchema.nullable(),
  fromField: fieldSchema.nullable(),
  toField: fieldSchema.nullable(),
});

const operandSchema = z.discriminatedUnion('type', [
  valueOperandSchema,
  fieldOperandSchema,
]);

const simplePredicateSchema = z
  .object({
    __typename: z.literal('SimplePredicate'),
    field: fieldSchema.nullable(),
    relOp: relOpSchema.nullable(),
    matchCase: z.boolean(),
    operand: operandSchema,
  })
  .superRefine((predicate, ctx) => {
    const { field, relOp, operand } = predicate;

    if (isNil(field)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Value is required',
        path: ['field'],
        fatal: true,
      });

      return z.NEVER;
    }

    if (isNil(relOp)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Value is required',
        path: ['relOp'],
        fatal: true,
      });

      return z.NEVER;
    }

    // in case of Between operator, fromOperand and toOperand should be required
    const isBetween = relOp?.key === RelationalOperatorKey.Between;
    const operandType = operand.type;

    if (operandType === OperandType.Value) {
      if (isBetween) {
        if (isNil(operand.fromOperand)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Value is required',
            path: ['operand.fromOperand'],
            fatal: true,
          });
        }

        if (isNil(operand.toOperand)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Value is required',
            path: ['operand.toOperand'],
            fatal: true,
          });
        }
      } else if (!operand?.operands?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Value is required',
          path: ['operand.operands'],
          fatal: true,
        });
      }
    } else {
      if (isBetween) {
        if (isNil(operand.fromField)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Value is required',
            path: ['operand.fromField'],
            fatal: true,
          });
        }

        if (isNil(operand.toField)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Value is required',
            path: ['operand.toField'],
            fatal: true,
          });
        }
      } else if (!operand.field) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Value is required',
          path: ['operand.field'],
          fatal: true,
        });
      }
    }
  });

const arrayObjectPredicateSchema = z.object({
  __typename: z.literal('ArrayObjectPredicate'),
  predicateElements: z.array(simplePredicateSchema),
});

// not using z.discriminatedUnion because it doesn't work with discriminated union of super refined
// https://github.com/colinhacks/zod/issues/2440
// The maintainer is going to propose a new syntax, similar to this implementation
// See: https://github.com/colinhacks/zod/discussions/2109
const predicateSchema = z
  .object({
    __typename: z
      .literal('SimplePredicate')
      .or(z.literal('ArrayObjectPredicate')),
  })
  .passthrough() // pass unrecognized props so we can narrow the type in the transform
  .transform((predicate, ctx) => {
    let result;
    if (predicate.__typename === 'SimplePredicate') {
      result = simplePredicateSchema.safeParse(predicate);
    } else if (predicate.__typename === 'ArrayObjectPredicate') {
      result = arrayObjectPredicateSchema.safeParse(predicate);
    } else {
      throw new UnreachableCaseError(predicate.__typename);
    }
    if (!result.success) {
      result.error.errors.forEach(ctx.addIssue);
      return z.NEVER;
    }
    return result.data;
  });

const logicOpSchema = z.object({
  key: z.enum([LogicalOperatorKey.And, LogicalOperatorKey.Or]),
  name: z.string(),
  __typename: z.literal('LogicalOperator'),
});

const conditionSchema = z.object({
  predicates: z.array(predicateSchema),
  logicOps: z.array(logicOpSchema),
});

const filterTypeEnum = z.enum([FilterType.Basic, FilterType.Named]);

const basicFilterSchema = z.object({
  type: z.literal(filterTypeEnum.enum.BASIC),
  conditions: z.array(conditionSchema),
  logicOps: z.array(logicOpSchema),
});

const namedFilterFieldsSchema = z
  .object<
    ZodProperties<
      Pick<INamedFilterFieldsFragment, 'id' | 'name' | '__typename'>
    >
  >({
    __typename: z.literal('NamedFilter'),
    name: z.string(),
    id: z.string(),
  })
  // We perform very limited validation over the Named Filter because React Hook Form does not edit its properties.
  // Checking for few mandatory properties should give enough safety. Moreover, React Hook Form performs complex type
  // manipulation to expand objects (e.g. {a: {b: 1}}) into path ("a.b") which easily reaches TypeScript limits, and we
  // get "TS2589: Type instantiation is excessively deep and possibly infinite" (e.g.
  // https://github.com/react-hook-form/react-hook-form/issues/6679). Version 8 of RHF aims to solve this performance
  // regression (https://github.com/orgs/react-hook-form/discussions/7389)
  .passthrough();

const namedFilterSchema = z.object({
  type: z.literal(filterTypeEnum.enum.NAMED),
  namedFilter: namedFilterFieldsSchema.nullable(),
});

export const filterWidgetSchema = z.discriminatedUnion('type', [
  basicFilterSchema,
  namedFilterSchema,
]);

type IFieldFormState = z.infer<typeof fieldSchema>;
type INamedFieldFormState = z.infer<typeof namedFilterFieldsSchema>;
export type IFilterOperand = z.infer<typeof operandSchema>;
export type IFilterPredicate = z.infer<typeof predicateSchema>;
export type ISimpleFilterPredicate = z.infer<typeof simplePredicateSchema>;
export type IFilterCondition = z.infer<typeof conditionSchema>;
export type IFilterLogicOp = z.infer<typeof logicOpSchema>;
export type IFilterWidgetState = z.infer<typeof filterWidgetSchema>;
export type IOperandValue = z.infer<typeof operandValueSchema>;

export const defaultAnyFieldValue = anyFieldValueSchema.parse({
  __typename: 'AnyFieldValue',
  name: 'Any',
});

// ====================================================================================================================
// To overcome RHF poor TypeScript performance, we define very slim and partial TS type for opaque objects (objects
// like field and named filter which properties RHF does not edit individually but treats them as one entity) in the
// form state. These helpers simply cast the form state to the actual object stored in there.

export const castToFieldFragment = (input: IFieldFormState) =>
  input as IFieldFieldsFragment;

export const castToOptionalFieldFragment = (input: IFieldFormState | null) =>
  input as IFieldFieldsFragment | null;

export const castToNamedFilterFragment = (input: INamedFieldFormState) =>
  input as INamedFilterFieldsFragment;

// ====================================================================================================================

export const defaultOperandState = freeze<IFilterOperand>(
  {
    type: OperandType.Value,
    operands: [],
    fromOperand: null,
    toOperand: null,
  },
  true,
);

export const defaultPredicate = freeze<ISimpleFilterPredicate>(
  {
    __typename: 'SimplePredicate',
    field: null,
    relOp: null,
    matchCase: true,
    operand: defaultOperandState,
  },
  true,
);

export const defaultCondition = freeze<IFilterCondition>(
  {
    predicates: [defaultPredicate],
    logicOps: [],
  },
  true,
);

export const defaultLogicOp = freeze<IFilterLogicOp>(
  {
    key: LogicalOperatorKey.And,
    name: LogicalOperatorKey.And,
    __typename: 'LogicalOperator',
  },
  true,
);

export const defaultBasicFilterWidgetValues = freeze<
  DefaultValues<IFilterWidgetState>
>(
  {
    type: FilterType.Basic,
    conditions: [defaultCondition],
    logicOps: [],
  },
  true,
);

export enum FilterWidgetFormFieldPathType {
  SimplePredicate = 'SIMPLE_PREDICATE',
  ArrayObjectPredicate = 'ARRAY_OBJECT_PREDICATE',
}

export type ISimplePredicateFormFieldPath = {
  type: FilterWidgetFormFieldPathType.SimplePredicate;
  conditionIndex: number;
  predicateIndex: number;
};

export type IArrayObjectPredicateFormFieldPath = {
  type: FilterWidgetFormFieldPathType.ArrayObjectPredicate;
  conditionIndex: number;
  predicateIndex: number;
  predicateElementIndex: number;
};

export type IPredicateFormFieldPath =
  | ISimplePredicateFormFieldPath
  | IArrayObjectPredicateFormFieldPath;
